import { useNavigate } from "react-router-dom";

import { LOGIN_QR_URL } from "@/constants/routes";
import useModal from "@/hooks/useModal";
import useViewMode, { useViewModeStore } from "@/hooks/useViewMode";
import SignUpPreview from "@/pages/Admin/components/SignUpPreview";
import MyPageWrapper from "@/pages/MyPage/components/MyPageLayout";
import useUserStore from "@/store/userStore";
import ModalLayout from "@/components/Layout/ModalLayout";

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="border-b border-gray-100 py-4 last:border-b-0">
    <div className="mb-1 text-xs font-medium text-gray-400">{label}</div>
    <div className="text-sm text-gray-800">{children}</div>
  </div>
);

const MyInfo = () => {
  const { userName, telNo, cmtNm, garNm, leafNm, roleId } = useUserStore();

  const roleBadge = {
    ADMIN: { label: "관리자", cls: "bg-red-100 text-red-700" },
    MINISTRY: { label: "교역자", cls: "bg-purple-100 text-purple-700" },
    LEADER: { label: "순장/리더", cls: "bg-blue-100 text-blue-700" },
  }[roleId] || { label: "일반 유저", cls: "bg-gray-100 text-gray-600" };

  return (
    <MyPageWrapper>
      <div className="w-full max-w-sm px-8 py-16 md:px-0">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col">
            <InfoRow label="이름">{userName || "-"}</InfoRow>
            <InfoRow label="권한">
              <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${roleBadge.cls}`}>
                {roleBadge.label}
              </span>
            </InfoRow>
            <InfoRow label="전화번호">{telNo || "-"}</InfoRow>
            <InfoRow label="소속">
              <div className="flex flex-col gap-0.5">
                {cmtNm ? <span><span className="text-gray-400">공동체 : </span>{cmtNm}</span> : null}
                {garNm ? <span><span className="text-gray-400">다락방 : </span>{garNm}</span> : null}
                {leafNm ? <span><span className="text-gray-400">순 : </span>{leafNm}</span> : null}
                {!cmtNm && !garNm && !leafNm && "-"}
              </div>
            </InfoRow>
          </div>
        </div>
        <div className="mt-4 text-center text-xs text-gray-400">
          회원정보 수정은 관리자에게 문의해주세요.
        </div>

        <ViewModeToggle />
      </div>
    </MyPageWrapper>
  );
};

const viewModeOptions: { mode: "admin" | "user" | "unapproved"; label: string; desc: string }[] = [
  { mode: "admin", label: "관리자 뷰", desc: "모든 기능 사용 가능" },
  { mode: "user", label: "일반 유저 뷰", desc: "승인된 일반 유저 시점" },
  { mode: "unapproved", label: "미승인 유저 뷰", desc: "회원가입 승인 대기 시점" },
];

const ViewModeToggle = () => {
  const navigate = useNavigate();
  const { isRealAdmin, viewMode } = useViewMode();
  const { setViewMode } = useViewModeStore();
  const signUpModal = useModal();

  if (!isRealAdmin) return null;

  const handleModeChange = (mode: "admin" | "user" | "unapproved") => {
    setViewMode(mode);
    if (mode === "unapproved") {
      navigate(LOGIN_QR_URL);
    }
  };

  return (
    <div className="mt-8 flex flex-col gap-2">
      <p className="text-xs font-medium text-gray-400">뷰 모드 전환</p>
      {viewModeOptions.map(({ mode, label, desc }) => (
        <button
          key={mode}
          type="button"
          onClick={() => handleModeChange(mode)}
          className={`w-full rounded-lg border px-4 py-3 text-left text-sm transition-all ${
            viewMode === mode
              ? "border-primary bg-primary/5 text-primary"
              : "border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100"
          }`}
        >
          <span className="font-medium">{label}</span>
          <span className="ml-2 text-xs opacity-60">{desc}</span>
        </button>
      ))}

      <button
        type="button"
        onClick={signUpModal.onOpen}
        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm text-gray-500 transition-all hover:bg-gray-100"
      >
        <span className="font-medium">회원가입 뷰</span>
        <span className="ml-2 text-xs opacity-60">신규 가입자가 보는 화면</span>
      </button>

      {signUpModal.isOpen && (
        <ModalLayout variant="primary" onClose={signUpModal.onClose} className="max-h-[90vh] overflow-y-auto">
          <SignUpPreview />
        </ModalLayout>
      )}
    </div>
  );
};

export default MyInfo;
